import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setShowFormPopup } from "../../../../actions/taskSidebar";
import { icon, tooltip } from "../../../../config";
import { number } from "../../../../config";
import CommonTabStrip from "../../../../shared/components/CommonTabStrip/CommonTabStrip";
import { getFormSubmission, getWorkflowFormById } from "../../../../shared/services/workflow.services";
import FormFiller from "../../../Forms/FormFiller/FormFiller";
import { FORM_TABS, FORM_TABS_KEYS } from "../../../Forms/FormsConstant";
import FormRenderer from "../../../Forms/Preview/FormRenderer";
import { FormCalculationProvider } from "../../../Forms/contexts";
import { getAllInitialResponsesFromContent, useFormSubmission } from "../../../Forms/forms.hooks";
import { getAttachmentObject } from "../Attachments/attachments.helper";
import "./../Description/descriptionPopup.scss";
import "./TaskForm.scss";
import TaskFormHeader from "./TaskFormHeader";

/**
 * Task Form Popup component - displays submitted form in read-only mode
 * @returns JSX
 * @author [Sarthak]
 */
const TaskForm = () => {
  const dispatch = useDispatch();
  const { task, showFormPopup } = useSelector((state) => state.taskSidebar);
  const [formData, setFormData] = useState(null);
  const [formResponsesFromSubmission, setFormResponsesFromSubmission] = useState({});
  const [smartChipResponsesFromSubmission, setSmartChipResponsesFromSubmission] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const popupRef = useRef(null);
  const [showFormFiller, setShowFormFiller] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formTab, setFormTab] = useState({
    isHiddenSection: true,
    selected: number.ZERO,
    ...FORM_TABS.SUBSECTIONS_OBJ,
  });

  // Use the form submission hook
  const { submitFormWithFiles } = useFormSubmission();

  const formContent = formData?.content || formData;

  /**
   * Fetch submitted form data when form popup opens and task has formId
   * @author [Sarthak]
   */
  useEffect(() => {
    fetchFormData();
  }, [task?.workflowFormId]);

  const fetchFormData = async () => {
    if (showFormPopup && task?.submittedFormId) {
      try {
        setLoading(true);
        setError(null);
        // Get submitted form data with filled values
        const submittedFormData = await getFormSubmission({
          submittedFormId: task?.submittedFormId,
        });
        if (submittedFormData) {
          setFormData(submittedFormData);
          setFormTab((prev) => ({
            ...prev,
            isHiddenSection: submittedFormData?.formId?.isHiddenSection ?? prev.isHiddenSection,
          }));
          const { initial, scResponses } = getAllInitialResponsesFromContent(submittedFormData?.content);
          setFormResponsesFromSubmission(initial);
          setSmartChipResponsesFromSubmission((prev) => ({ ...prev, ...scResponses }));
        } else {
          setError("Form data not found");
        }
      } catch (err) {
        console.error("Error fetching form data:", err);
        setError("Failed to load form data");
      } finally {
        setLoading(false);
        setShowFormFiller(false);
      }
    } else if (showFormPopup && !task?.submittedFormId && task?.workflowFormId) {
      const workflowFormData = await getWorkflowFormById({ id: task?.workflowFormId });
      setFormData(workflowFormData);
      if (workflowFormData) {
        setFormTab((prev) => ({
          ...prev,
          isHiddenSection: workflowFormData?.isHiddenSection ?? prev.isHiddenSection,
        }));
        setShowFormFiller(true);
      }
    }
  };

  /**
   * Closes the form popup
   * @author [Sarthak]
   */
  const handleClose = () => {
    dispatch(setShowFormPopup(false));
    setFormData(null);
    setError(null);
    setFormResponsesFromSubmission({});
    setSmartChipResponsesFromSubmission({});
  };

  /**
   * Handles form submission using the form submission hook
   * @param {Object} formResponses - The form response data
   */
  const handleFormSubmit = async (formResponses, getCalculationValue, formTab) => {
    try {
      await submitFormWithFiles({
        formData,
        formResponses,
        taskId: task?.taskId,
        IsRuleBasedTask: task?.IsRuleBasedTask,
        getAttachmentObject,
        onSuccess: (response) => {
          // Close the form filler after successful submission
          setShowFormFiller(false);
          setShowEditForm(false);
          fetchFormData();
          handleClose(); // Also close the main popup
        },
        onError: (error) => {
          setShowFormFiller(false);
          handleClose();
          console.error("Form submission error:", error);
        },
        taskHistoryId: task?.taskHistoryId,
        getCalculationValue,
        submissionType: showEditForm ? "update" : "submit",
        formTab,
      });
    } catch (error) {
      console.error("Error in form submission:", error);
      // Error handling is done in the hook
    }
  };

  /**
   * Renders loading state
   */
  const renderLoading = () => (
    <div className='form-popup-loading d-flex justify-content-center align-items-center p-4'>
      <div className='d-flex flex-column align-items-center'>
        <div className='spinner-border text-primary mb-2' role='status'>
          <span className='sr-only'>Loading...</span>
        </div>
        <span>Loading form data...</span>
      </div>
    </div>
  );

  /**
   * Renders error state
   */
  const renderError = () => (
    <div className='form-popup-error d-flex justify-content-center align-items-center p-4'>
      <div className='alert alert-danger d-flex align-items-center' role='alert'>
        <span className='me-2'>{icon.ERROR}</span>
        <span>{error}</span>
      </div>
    </div>
  );

  /**
   * Renders form content
   */
  const renderFormContent = () => {
    if (!formData && task?.submittedFormId) {
      return (
        <div className='form-popup-empty d-flex justify-content-center align-items-center p-4'>
          <span>No form data available</span>
        </div>
      );
    }

    const formRendererElement = (
      <FormRenderer
        formData={formContent}
        formResponses={formResponsesFromSubmission}
        smartChipResponses={smartChipResponsesFromSubmission}
        setSmartChipResponses={setSmartChipResponsesFromSubmission}
        isInteractive={false}
        showResponses={true}
        submitDetails={formData?.submitterDetails}
        formTab={formTab}
      />
    );

    const renderFormOrTabs = () => {
      if (formTab.isHiddenSection) {
        return (
          <CommonTabStrip
            tabs={[
              { title: FORM_TABS.SUBSECTIONS_OBJ.title, content: formRendererElement },
              { title: FORM_TABS.HIDDEN_SUBSECTIONS_OBJ.title, content: formRendererElement },
            ]}
            selectedTab={formTab.selected}
            onTabSelect={(e) =>
              setFormTab({
                ...formTab,
                selected: e.selected,
                ...FORM_TABS[FORM_TABS_KEYS[e?.selected]],
              })
            }
            contentClassName='form-popup-content'
            animation={false}
            tabStripProps={{ keepTabsMounted: true, renderAllContent: true }}
          />
        );
      }
      return formRendererElement;
    };

    return (
      <div className='form-popup-content'>{task?.submittedFormId && <FormCalculationProvider>{renderFormOrTabs()}</FormCalculationProvider>}</div>
    );
  };

  const handleFormClose = () => {
    if (showEditForm) {
      setShowEditForm(false);
    } else {
      setShowFormFiller(false);
      setShowEditForm(false);
      handleClose();
    }
  };

  if (!showFormPopup) {
    return null;
  }

  return (
    <div>
      {task?.submittedFormId && (
        <Popup show={showFormPopup} className='desc-popup-container' popupClass='desc-popup-child task-form-popup' animate={false} ref={popupRef}>
          <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
            {/* Popup Header */}
            <TaskFormHeader formData={formData} onEditForm={() => setShowEditForm(true)} onClose={handleClose} formTab={formTab} />
          </Tooltip>

          {/* Popup Body */}
          <div className='task-form-container'>
            {loading && renderLoading()}
            {error && renderError()}
            {!loading && !error && renderFormContent()}
          </div>
        </Popup>
      )}
      <FormFiller
        isOpen={showFormFiller || showEditForm}
        formData={formData}
        formTitle={formData?.title || formData?.content?.title}
        formDescription={formData?.description || formData?.content?.description}
        onSubmit={handleFormSubmit}
        formId={formData?.content?.id || formData?.formId?._id}
        onClose={handleFormClose}
        formTab={formTab}
        submitDetails={formData?.submitterDetails}
      />
    </div>
  );
};

export default TaskForm;
